import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, Pause, Play, X } from 'lucide-react';
import { CLAIRADS_URL, CONTACT_EMAIL, contactHref } from '../../config/publicLinks';
import { useLandingMotion, LandingMotionControls, readMotionPaused } from '../landing/LandingMotion';
import { useLandingRouteEntry } from '../landing/LandingNavigation';
import './ClairLabsLandingPage.css';

const SYSTEM_PARTS = [
  { id: 'north-star', label: 'North Star', title: 'Un objetivo claro', text: 'El resultado de negocio organiza cada decisión. Antes de elegir tecnología, definimos qué queremos cambiar y cómo reconoceremos el avance.' },
  { id: 'navigator', label: 'Navigator', title: 'Dirección humana', text: 'Estrategia, criterio y responsabilidad permanecen visibles. La inteligencia artificial amplifica la capacidad de un equipo con una dirección compartida.' },
  { id: 'stars', label: 'Stars', title: 'Capacidades especializadas', text: 'Agentes, modelos y herramientas con un propósito concreto. Elegimos las capacidades necesarias para cada proyecto, desde el análisis hasta la creación.' },
  { id: 'connections', label: 'Connections', title: 'Contexto compartido', text: 'Cada capacidad trabaja con la información que necesita y en la secuencia correcta. La orquestación convierte herramientas individuales en una experiencia coherente.' },
  { id: 'orbit', label: 'Orbit', title: 'Aprendizaje continuo', text: 'Observamos los resultados, evaluamos las decisiones y llevamos lo aprendido al siguiente ciclo. El sistema evoluciona junto con el objetivo.' },
  { id: 'constellation', label: 'Constellation', title: 'Un sistema completo', text: 'Múltiples inteligencias que trabajan juntas. Estrategia, ejecución y evaluación forman parte de una misma solución, diseñada alrededor de tu negocio.' },
];

const PROCESS = [
  ['01', 'Entender el objetivo.', 'Definimos el problema, el contexto y el resultado que debe mover el sistema.'],
  ['02', 'Diseñar la solución.', 'Elegimos las inteligencias, herramientas y capacidades que realmente hacen falta.'],
  ['03', 'Dar dirección.', 'La estrategia y el criterio permanecen bajo control humano.'],
  ['04', 'Crear y ejecutar.', 'Los sistemas analizan, conectan y producen con una dirección compartida.'],
  ['05', 'Aprender e iterar.', 'Evaluamos lo conseguido e incorporamos cada aprendizaje al siguiente ciclo.'],
];

const CAPABILITIES = [
  { id: 'products', title: 'AI Products', heading: 'Inteligencia dentro de la experiencia.', text: 'Concebimos productos en los que la IA ayuda a hacer algo concreto: entender, crear o decidir con más contexto.', examples: ['Asistentes integrados', 'Experiencias con contexto', 'Herramientas de creación'], image: '/landing/concepts/labs/ai-products.webp', smallImage: '/landing/concepts/labs/ai-products-800.webp', width: 1600, height: 900, alt: 'Objeto modular de cerámica y aluminio con una pieza elevada que revela un núcleo óptico.', caption: 'Concepto: inteligencia integrada' },
  { id: 'agents', title: 'Agentic Systems', heading: 'Especialistas que trabajan juntos.', text: 'Diseñamos agentes con responsabilidades, herramientas y límites claros. La colaboración parte de un objetivo compartido y conserva la supervisión humana.', examples: ['Investigación asistida', 'Análisis de información', 'Coordinación de tareas'], image: '/landing/concepts/labs/agentic-systems.webp', smallImage: '/landing/concepts/labs/agentic-systems-800.webp', width: 1600, height: 900, alt: 'Mecanismos de latón, cerámica y grafito coordinados alrededor de una misma pieza.', caption: 'Concepto: trabajo coordinado' },
  { id: 'multimodal', title: 'Multimodal Experiences', heading: 'Más formas de comprender y crear.', text: 'Combinamos texto, imagen, voz y video cuando el problema lo necesita. El contexto acompaña a la idea al pasar de un formato a otro.', examples: ['Briefs con referencias', 'Experiencias de voz', 'Narrativas audiovisuales'], image: '/landing/concepts/labs/multimodal-experiences.webp', smallImage: '/landing/concepts/labs/multimodal-experiences-800.webp', width: 1600, height: 900, alt: 'Prisma óptico que refracta luz sobre ondas de cerámica y una superficie textil.', caption: 'Concepto: lenguajes conectados' },
  { id: 'automation', title: 'Intelligent Automation', heading: 'Del contexto a la acción.', text: 'Conectamos modelos, datos y aplicaciones para dar continuidad al trabajo. Cada automatización responde a una necesidad y a reglas explícitas.', examples: ['Clasificación documental', 'Preparación de información', 'Procesos con revisión humana'], image: '/landing/concepts/labs/intelligent-automation.webp', smallImage: '/landing/concepts/labs/intelligent-automation-800.webp', width: 1600, height: 900, alt: 'Cuatro etapas de ensamblaje sobre bases de precisión, de piezas sueltas a un módulo completo.', caption: 'Concepto: del proceso al resultado' },
  { id: 'creative', title: 'Creative Intelligence', heading: 'Una idea. Una dirección de marca.', text: 'La inteligencia acelera la exploración y la producción. El criterio creativo mantiene la intención, la identidad y el detalle en cada pieza.', examples: ['Conceptos de campaña', 'Contenido de marca', 'Producción de imagen y video'], image: '/landing/concepts/labs/creative-intelligence.webp', smallImage: '/landing/concepts/labs/creative-intelligence-800.webp', width: 1600, height: 900, alt: 'Composición de papel plegado, pigmento coral y filtros ópticos con luz cálida.', caption: 'Concepto: exploración creativa' },
  { id: 'prototyping', title: 'AI Prototyping', heading: 'De una hipótesis a algo que probar.', text: 'Convertimos ideas en experiencias funcionales para contrastar supuestos antes de ampliar la inversión y el alcance.', examples: ['Pruebas de concepto', 'Validación de flujos', 'Prototipos de producto'], image: '/landing/concepts/labs/ai-prototyping.webp', smallImage: '/landing/concepts/labs/ai-prototyping-800.webp', width: 1600, height: 900, alt: 'Tres etapas de un prototipo físico: forma inicial, estructura y piezas refinadas en despiece.', caption: 'Concepto: de la hipótesis a la forma' },
];

const NAV_LINKS = [
  ['Visión', 'vision'],
  ['Cómo trabajamos', 'como-trabajamos'],
  ['Ecosistema', 'ecosistema'],
  ['ClairAds', 'clairads'],
  ['Contacto', 'contacto'],
];

function ClairLabsBrand({ footer = false }) {
  return (
    <Link className={`clair-brand${footer ? ' is-footer' : ''}`} to="/" aria-label="Clair Labs, inicio">
      <img src="/landing/clairlabs/clairlabs-mark-160.png" alt="" width="42" height="42" />
      <span><strong>Clair Labs</strong>{footer && <small>Connected Intelligence</small>}</span>
    </Link>
  );
}

function ConstellationSystem() {
  const [active, setActive] = useState(0);
  return (
    <div className="clair-system-index">
      {SYSTEM_PARTS.map((part, index) => (
        <article key={part.id} className={active === index ? 'is-open' : ''}>
          <h3><button type="button" aria-expanded={active === index} aria-controls={`clair-system-${part.id}`} onClick={() => setActive(active === index ? null : index)}>
            <span className="clair-index-number">0{index + 1}</span><span>{part.title}</span><span className="clair-index-toggle" aria-hidden="true">{active === index ? '−' : '+'}</span>
          </button></h3>
          <div className="clair-system-answer" id={`clair-system-${part.id}`} hidden={active !== index}>
            <span>{part.label}</span><p>{part.text}</p>
          </div>
        </article>
      ))}
    </div>
  );
}

function CapabilityExplorer() {
  const [active, setActive] = useState(0);
  const [vertical, setVertical] = useState(() => !window.matchMedia('(max-width: 860px)').matches);
  const tabs = useRef([]);
  useEffect(() => {
    const compact = window.matchMedia('(max-width: 860px)');
    const onChange = (event) => setVertical(!event.matches);
    compact.addEventListener?.('change', onChange);
    return () => compact.removeEventListener?.('change', onChange);
  }, []);
  const onTabKeyDown = (event, index) => {
    let next;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = (index + 1) % CAPABILITIES.length;
    else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = (index - 1 + CAPABILITIES.length) % CAPABILITIES.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = CAPABILITIES.length - 1;
    else return;
    event.preventDefault();
    setActive(next);
    tabs.current[next]?.focus({ preventScroll: true });
    tabs.current[next]?.scrollIntoView?.({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
  };
  return (
    <div className="clair-capability-explorer" data-reveal>
      <div className="clair-capability-navigation">
        <div className="clair-capability-tabs" role="tablist" aria-label="Explorar capacidades de Clair Labs" aria-orientation={vertical ? 'vertical' : 'horizontal'}>
          {CAPABILITIES.map((capability, index) => <button key={capability.id} ref={(element) => { tabs.current[index] = element; }} type="button" role="tab" id={`clair-capability-tab-${capability.id}`} aria-controls={`clair-capability-panel-${capability.id}`} aria-selected={active === index} tabIndex={active === index ? 0 : -1} onClick={() => setActive(index)} onKeyDown={(event) => onTabKeyDown(event, index)}><span>0{index + 1}</span><strong>{capability.title}</strong></button>)}
        </div>
        <p className="clair-capability-navigation-note">Una capacidad por sí sola.<br />Más posibilidades al conectarlas.</p>
      </div>
      <div className="clair-capability-panels">
        {CAPABILITIES.map((capability, index) => <section key={capability.id} role="tabpanel" id={`clair-capability-panel-${capability.id}`} aria-labelledby={`clair-capability-tab-${capability.id}`} tabIndex={0} hidden={active !== index}>
          {active === index && <div className="clair-capability-panel-content">
            <figure className="clair-capability-art"><img src={capability.image} srcSet={`${capability.smallImage} 800w, ${capability.image} 1600w`} sizes="(max-width: 600px) calc(100vw - 40px), (max-width: 860px) calc(100vw - 48px), (max-width: 1160px) 55vw, 58vw" alt={capability.alt} width={capability.width} height={capability.height} loading="lazy" decoding="async" /><figcaption>{capability.caption}</figcaption></figure>
            <div className="clair-capability-detail"><h3>{capability.heading}</h3><p>{capability.text}</p><div className="clair-capability-examples"><span>Aplicaciones posibles</span><ul>{capability.examples.map((example) => <li key={example}>{example}</li>)}</ul></div></div>
          </div>}
        </section>)}
      </div>
    </div>
  );
}

function Methodology() {
  const [active, setActive] = useState(0);
  const steps = useRef([]);
  useEffect(() => {
    if (!('IntersectionObserver' in window)) return undefined;
    const visible = new Set();
    let observer;
    const observeSteps = () => {
      observer?.disconnect();
      visible.clear();
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visible.add(entry.target);
          else visible.delete(entry.target);
        });
        const current = [...visible].sort((a, b) => Math.abs(a.getBoundingClientRect().top - window.innerHeight * .25) - Math.abs(b.getBoundingClientRect().top - window.innerHeight * .25))[0];
        if (current) setActive(Number(current.dataset.chapter));
      }, { rootMargin: `-${Math.round(window.innerHeight * .15)}px 0px -${Math.round(window.innerHeight * .45)}px`, threshold: 0 });
      steps.current.forEach((step) => { if (step) observer.observe(step); });
    };
    observeSteps();
    window.addEventListener('resize', observeSteps, { passive: true });
    return () => { observer?.disconnect(); window.removeEventListener('resize', observeSteps); };
  }, []);
  return (
    <section id="como-trabajamos" className="clair-section clair-process-section" data-motion-section>
      <div className="clair-shell clair-process-layout">
        <div className="clair-process-intro" data-reveal>
          <p className="clair-eyebrow">Cómo trabajamos</p><h2>Una dirección.<br /><span>Todo el camino.</span></h2><p>La tecnología es el motor. El criterio es humano. El objetivo da sentido a cada paso.</p>
          <div className="clair-process-chapter" aria-label={`Paso visible: ${active + 1} de ${PROCESS.length}`}><span className="clair-process-chapter-count">{PROCESS[active][0]}<small> / 05</small></span><span>{PROCESS[active][1]}</span></div>
          <nav className="clair-process-chapters" aria-label="Pasos de nuestra metodología">{PROCESS.map(([number, title], index) => <a key={number} href={`#clair-process-step-${number}`} aria-label={`Paso ${number}: ${title}`} aria-current={active === index ? 'step' : undefined} onClick={() => setActive(index)}>{number}</a>)}</nav>
          <p className="clair-process-principle">AI-native. Human-directed.<br /><span>Contexto real, criterio visible y aprendizaje continuo.</span></p>
        </div>
        <ol className="clair-process-list">{PROCESS.map(([number, title, text], index) => <li key={number} id={`clair-process-step-${number}`} ref={(element) => { steps.current[index] = element; }} data-chapter={index} className={active === index ? 'is-current' : ''} data-reveal><span>{number}</span><div><h3>{title}</h3><p>{text}</p></div></li>)}</ol>
      </div>
    </section>
  );
}

function ClairAdsPreviewVideo() {
  const videoRef = useRef(null);
  const pausedByUser = useRef(false);
  const inView = useRef(false);
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    let motionPaused = readMotionPaused();
    const syncPlayback = () => {
      if (inView.current && !document.hidden && !pausedByUser.current && !preference.matches && !motionPaused) video.play().catch(() => {});
      else video.pause();
    };
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver(([entry]) => {
      inView.current = entry.isIntersecting;
      syncPlayback();
    }, { threshold: 0.2 }) : null;
    observer?.observe(video);
    const onMotionChange = (event) => { motionPaused = Boolean(event.detail?.paused); syncPlayback(); };
    window.addEventListener('landing-motion-change', onMotionChange);
    document.addEventListener('visibilitychange', syncPlayback);
    preference.addEventListener?.('change', syncPlayback);
    return () => {
      observer?.disconnect();
      window.removeEventListener('landing-motion-change', onMotionChange);
      document.removeEventListener('visibilitychange', syncPlayback);
      preference.removeEventListener?.('change', syncPlayback);
      video.pause();
    };
  }, []);
  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) { pausedByUser.current = false; video.play().catch(() => {}); }
    else { pausedByUser.current = true; video.pause(); }
  };
  return (
    <>
      <video ref={videoRef} muted loop playsInline preload="none" poster="/landing/velocity/clairads-showreel-poster.jpg" aria-label="Muestra audiovisual creada por ClairAds" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}>
        <source src="/landing/velocity/clairads-showreel-master.mp4" type="video/mp4" />
      </video>
      <div className="clair-film-caption"><span>ClairAds / Creative Intelligence</span><button type="button" onClick={togglePlayback} aria-label={playing ? 'Pausar muestra audiovisual' : 'Reproducir muestra audiovisual'}>{playing ? <Pause size={16} /> : <Play size={16} />}<span>{playing ? 'Pausar' : 'Reproducir'}</span></button></div>
    </>
  );
}

function ClairAdsSpotlight() {
  return (
    <section id="clairads" className="clair-section clair-ads-section" data-motion-section>
      <div className="clair-shell">
        <div className="clair-ads-intro" data-reveal>
          <div><p className="clair-eyebrow">Inteligencia aplicada</p><h2>ClairAds.<br /><span>De la idea al impacto.</span></h2></div>
          <div className="clair-ads-copy"><p>Estrategia, identidad de marca y producción multimodal. Un departamento de marketing AI-native, dirigido por personas.</p><Link className="clair-text-link" to={CLAIRADS_URL}>Explorar ClairAds</Link></div>
        </div>
        <div className="clair-ads-media" data-reveal><ClairAdsPreviewVideo /></div>
        <div className="clair-ads-caption"><span>Creative &amp; Marketing Intelligence</span><p>Estrategia. Marca. Imagen. Video. Campañas.</p></div>
      </div>
    </section>
  );
}

export default function ClairLabsLandingPage() {
  useLandingRouteEntry();
  const rootRef = useLandingMotion();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('vision');
  const sentinelRef = useRef(null);
  const menuButtonRef = useRef(null);
  const navRef = useRef(null);

  useEffect(() => {
    document.title = 'Clair Labs | Connected Intelligence';
    const description = document.querySelector('meta[name="description"]');
    description?.setAttribute('content', 'Clair Labs construye sistemas AI-native que conectan estrategia, inteligencia, creatividad y ejecución para transformar objetivos de negocio en resultados.');
  }, []);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !('IntersectionObserver' in window)) return undefined;
    const observer = new IntersectionObserver(([entry]) => setScrolled(!entry.isIntersecting));
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);


  useEffect(() => {
    const sections = NAV_LINKS.map(([, id]) => document.getElementById(id)).filter(Boolean);
    if (!('IntersectionObserver' in window)) return undefined;
    let observer;
    const observeSections = () => {
      observer?.disconnect();
      observer = new IntersectionObserver((entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActiveSection(visible.target.id);
      }, { rootMargin: `-${Math.round(window.innerHeight * .22)}px 0px -${Math.round(window.innerHeight * .62)}px`, threshold: [0.05, 0.2, 0.5] });
      sections.forEach((section) => observer.observe(section));
    };
    observeSections();
    window.addEventListener('resize', observeSections, { passive: true });
    return () => { observer?.disconnect(); window.removeEventListener('resize', observeSections); };
  }, []);

  useEffect(() => {
    if (!menuOpen) {
      document.body.style.removeProperty('overflow');
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusFrame = window.requestAnimationFrame(() => navRef.current?.querySelector('a')?.focus());
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') { setMenuOpen(false); menuButtonRef.current?.focus(); }
      if (event.key !== 'Tab') return;
      const items = [...(navRef.current?.querySelectorAll('a') || []), menuButtonRef.current].filter(Boolean);
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [menuOpen]);

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 1161px)');
    const closeOnDesktop = (event) => { if (event.matches) setMenuOpen(false); };
    desktop.addEventListener?.('change', closeOnDesktop);
    return () => desktop.removeEventListener?.('change', closeOnDesktop);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="clair-site" ref={rootRef}>
      <a className="clair-skip" href="#clair-main">Saltar al contenido</a>
      <span ref={sentinelRef} className="clair-scroll-sentinel" aria-hidden="true" />
      <header className={`clair-header${scrolled ? ' is-scrolled' : ''}`}>
        <div className="clair-shell clair-nav">
          <ClairLabsBrand />
          <nav ref={navRef} id="clair-mobile-menu" className={`clair-links${menuOpen ? ' is-open' : ''}`} aria-label="Navegación principal">
            {NAV_LINKS.map(([label, id]) => <a key={id} className={activeSection === id ? 'is-active' : ''} href={`#${id}`} onClick={closeMenu}>{label}</a>)}
          </nav>
          <Link className="clair-nav-cta" to={CLAIRADS_URL}>Explorar ClairAds</Link>
          <button ref={menuButtonRef} className="clair-menu" type="button" aria-expanded={menuOpen} aria-controls="clair-mobile-menu" aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'} onClick={() => setMenuOpen((value) => !value)}>{menuOpen ? <X size={21} /> : <Menu size={21} />}</button>
        </div>
      </header>

      <main id="clair-main" tabIndex={-1}>
        <section id="vision" className="clair-hero" data-motion-section data-depth-scene>
          <picture className="clair-hero-backdrop" data-depth-layer>
            <source media="(max-width: 600px)" srcSet="/landing/experience/connected-intelligence-mobile.webp" />
            <img src="/landing/experience/connected-intelligence.webp" alt="" width="1672" height="941" fetchpriority="high" />
          </picture>
          <div className="clair-shell clair-hero-content">
            <div className="clair-hero-copy">
              <p className="clair-eyebrow">Connected Intelligence</p>
              <h1>Clair Labs</h1>
              <p className="clair-hero-promise">La inteligencia<br />toma forma.</p>
              <p className="clair-hero-description">Conectamos estrategia, agentes y creatividad para transformar objetivos reales en nuevas posibilidades.</p>
              <div className="clair-actions"><a className="clair-button is-primary" href="#sistema">Conoce nuestra visión</a><Link className="clair-button is-secondary" to={CLAIRADS_URL}>Explorar ClairAds</Link></div>
            </div>
          </div>
        </section>

        <section id="tesis" className="clair-section clair-thesis" data-motion-section>
          <div className="clair-shell">
            <p className="clair-eyebrow" data-reveal>Una forma distinta de construir</p>
            <h2 data-reveal>Las mejores ideas<br />no trabajan solas.</h2>
            <div className="clair-thesis-bottom" data-reveal><p className="clair-thesis-statement">El valor está en<br /><span>cómo las conectas.</span></p><p>Un modelo aislado no constituye una estrategia. Diseñamos la relación entre inteligencia, contexto, tecnología y dirección humana para resolver objetivos reales.</p></div>
          </div>
        </section>

        <section id="sistema" className="clair-section clair-system-section" data-motion-section>
          <div className="clair-shell clair-system-layout">
            <div className="clair-system-intro" data-reveal><p className="clair-eyebrow">The Clair Constellation System</p><h2>Cada parte<br />tiene un propósito.</h2><p>Conectamos las capacidades necesarias alrededor de un mismo objetivo. Así diseñamos inteligencia que trabaja contigo.</p><p className="clair-system-signature"><span>Especialización.</span><span>Conexión.</span><span>Dirección.</span></p></div>
            <ConstellationSystem />
          </div>
        </section>

        <section id="construimos" className="clair-section clair-capabilities-section" data-motion-section>
          <div className="clair-shell">
            <div className="clair-capabilities-heading" data-reveal><p className="clair-eyebrow">Lo que construimos</p><h2>De lo posible<br /><span>a lo tangible.</span></h2><p>Productos, sistemas y experiencias concebidos alrededor de la inteligencia.</p></div>
            <CapabilityExplorer />
          </div>
        </section>

        <ClairAdsSpotlight />

        <Methodology />

        <section id="ecosistema" className="clair-section clair-ecosystem-section" data-motion-section>
          <div className="clair-shell">
            <div className="clair-ecosystem-heading" data-reveal><p className="clair-eyebrow">El ecosistema Clair</p><h2>Una visión compartida.<br /><span>Aplicaciones concretas.</span></h2></div>
            <div className="clair-ecosystem-editorial" data-reveal>
              <article><img src="/landing/clairlabs/clairlabs-mark-160.png" alt="" width="64" height="64" /><span>La compañía</span><h3>Clair Labs</h3><p>Diseñamos la inteligencia, los productos y los sistemas que hacen posible cada experiencia.</p></article>
              <article><img src="/clairads-logo-128.png" alt="" width="64" height="64" /><span>La primera constelación</span><h3>ClairAds</h3><p>Aplicamos esa visión a la creatividad y el marketing: del brief a contenido y campañas con dirección de marca.</p><Link className="clair-text-link" to={CLAIRADS_URL}>Descubrir ClairAds</Link></article>
            </div>
          </div>
        </section>

        <section id="contacto" className="clair-final-section" data-motion-section>
          <div className="clair-shell clair-final-copy" data-reveal><p className="clair-eyebrow">Construyamos lo que sigue</p><h2>Tu próxima<br />gran posibilidad.</h2><p>Si existe un objetivo que la inteligencia artificial puede ayudarnos a alcanzar, empecemos por una conversación.</p><a className="clair-button is-primary" href={contactHref('Quiero construir con Clair Labs')}>Hablemos de tu proyecto</a><a className="clair-email" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></div>
        </section>
      </main>

      <footer className="clair-footer">
        <div className="clair-shell clair-footer-grid"><ClairLabsBrand footer /><nav aria-label="Enlaces del pie de página"><a href="#vision">Clair Labs</a><Link to={CLAIRADS_URL}>ClairAds</Link><a href={`mailto:${CONTACT_EMAIL}`}>Contacto</a></nav><a className="clair-footer-email" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></div>
        <div className="clair-shell clair-footer-bottom"><span>© {new Date().getFullYear()} Clair Labs.</span><LandingMotionControls /><span>Connected Intelligence.</span></div>
      </footer>
    </div>
  );
}
